import { BehaviorSubject, Observable } from 'rxjs';

import { Injectable } from '@angular/core';

import { MenuItem, MenuSection } from '../models/menu-item.model';

@Injectable({
  providedIn: 'root',
})
export class MenuRegistryService {
  private menuItems = new Map<string, MenuItem[]>();
  private menuSections = new BehaviorSubject<MenuSection[]>([]);

  /**
   * Register menu items for a specific module
   * @param moduleName - The name of the module registering menu items
   * @param items - Array of menu items to register
   */
  registerModule(section: MenuSection): void {
    this.updateMenuSections(section);
  }

  /**
   * Get all menu items from all registered modules
   */
  getAllMenuItems(): MenuItem[] {
    return Array.from(this.menuItems.values()).flat();
  }

  /**
   * Get menu items by section/type
   * @param section - The section/type to filter by
   */
  getMenuItemsBySection(section: string): MenuItem[] {
    return this.getAllMenuItems().filter(item => item.type === section);
  }

  /**
   * Get menu items for a specific module
   * @param moduleName - The name of the module
   */
  getMenuItemsByModule(moduleName: string): MenuItem[] {
    return this.menuItems.get(moduleName) || [];
  }

  /**
   * Get menu sections as an observable
   */
  getMenuSections(): Observable<MenuSection[]> {
    return this.menuSections.asObservable();
  }

  /**
   * Get current menu sections value
   */
  getCurrentMenuSections(): MenuSection[] {
    return this.menuSections.value;
  }

  /**
   * Find a specific menu item by ID
   * @param id - The menu item ID to find
   */
  findMenuItemById(id: string): MenuItem | undefined {
    return this.getAllMenuItems().find(item => item.id === id);
  }

  /**
   * Update menu sections based on registered items
   */
  private updateMenuSections(newSection: MenuSection): void {
    const sections = this.getCurrentMenuSections();
    sections.sort((a, b) => a.order - b.order);
    sections.push(newSection);
    this.menuSections.next(sections);
  }
}
